import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import config from './config';

// Get the current user's Cognito ID token
export async function getIdToken() {
    const { tokens } = await fetchAuthSession();
    return tokens.idToken.toString();
}

// Fetch attended + performed + worked status for a BATCH of event IDs in one call.
// Optional artistId (Artist "Seen" Status, 2026-09-06) scopes an additional
// per-event "caught" flag for that specific artist — omit it and the response
// shape/behavior is identical to before (caught comes back false on every entry).
// Returns { [event_id]: { attended, performed, worked, caught } }
export async function fetchBatchEventStatus(eventIds, artistId) {
    const token = await getIdToken();
    const payload = { event_ids: eventIds };
    if (artistId !== undefined && artistId !== null) {
        payload.artist_id = artistId;
    }
    const res = await axios.post(`${config.API_URL}/user/events/batch`, payload, {
        headers: { Authorization: token }
    });
    return res.data.status_map || {};
}

// Fetch every artist_id the logged-in user has "seen" (Caught, Performed, or
// Worked — see Artist "Seen" Status spec, 2026-09-06). Used by ArtistList.js's
// Seen/Not Seen filter. Returns [] on failure/logged-out — callers should treat
// that as "nothing known to be seen" rather than surfacing an error.
// Returns number[] of artist_id
export async function fetchSeenArtistIds() {
    const token = await getIdToken();
    const res = await axios.get(`${config.API_URL}/user/artists/seen`, {
        headers: { Authorization: token }
    });
    return res.data.seen_artist_ids || [];
}

// Fetch every venue_id the logged-in user has "visited" (Attended, Performed,
// or Worked any event at that venue — see SHARC_Spec_Venue_Browse_2026-09-24).
// Used by VenueList.js's Visited/Not Visited filter. Same best-effort contract
// as fetchSeenArtistIds: callers treat a failure/logged-out as "nothing known
// to be visited" rather than surfacing an error.
// Returns number[] of venue_id
export async function fetchVisitedVenueIds() {
    const token = await getIdToken();
    const res = await axios.get(`${config.API_URL}/user/venues/visited`, {
        headers: { Authorization: token }
    });
    return res.data.visited_venue_ids || [];
}

// Fetch every festival_id the logged-in user has "attended" (Attended,
// Performed, or Worked any event at that festival — see
// SHARC_Spec_Festival_Browse_2026-10-07). Used by FestivalList.js's
// Attended/Not Attended filter. Same best-effort contract as
// fetchSeenArtistIds: callers treat a failure/logged-out as "nothing known
// to be attended" rather than surfacing an error. Only leaf festivals are
// ever returned — events are never tagged with a series.
// Returns number[] of festival_id
export async function fetchAttendedFestivalIds() {
    const token = await getIdToken();
    const res = await axios.get(`${config.API_URL}/user/festivals/attended`, {
        headers: { Authorization: token }
    });
    return res.data.attended_festival_ids || [];
}

// Fetch attended + performed + worked + artist caught/missed status for a single event
// Returns { attended, performed, worked, artist_status, event_id }
export async function fetchEventStatus(eventId) {
    const token = await getIdToken();
    const res = await axios.get(`${config.API_URL}/user/event/${eventId}`, {
        headers: { Authorization: token }
    });
    return res.data;
}

// Toggle attended for an event, returns { attended, performed, worked, event_id, artist_status }
export async function toggleAttended(eventId) {
    const token = await getIdToken();
    const res = await axios.post(`${config.API_URL}/user/attended`, { event_id: eventId }, {
        headers: { Authorization: token }
    });
    return res.data;
}

// Toggle caught/missed for an artist at an event, returns { caught, event_id, artist_id }
export async function toggleCaught(eventId, artistId) {
    const token = await getIdToken();
    const res = await axios.post(`${config.API_URL}/user/caught`, { event_id: eventId, artist_id: artistId }, {
        headers: { Authorization: token }
    });
    return res.data;
}

/**
 * Resolve the display status for an event given the three participation flags.
 *
 * Priority: Performed > Worked > Attended > none
 *
 * @param {boolean} performed - user's linked member is in event_artist_members for this event
 * @param {boolean} worked    - user's linked member is in event_production_members for this event
 * @param {boolean} attended  - user has a record in user_events for this event
 * @returns {'performed' | 'worked' | 'attended' | 'none'}
 */
export function resolveEventStatus(performed, worked, attended) {
    if (performed) return 'performed';
    if (worked)    return 'worked';
    if (attended)  return 'attended';
    return 'none';
}

// ── Deletion capability ─────────────────────────────────────────────────────

// Returns true if the current user is a member of the Cognito "admins" group.
// This is a UX convenience check only — the real enforcement happens server-side
// in each admin Lambda (getDeletionQueue, permanentlyDeleteRequests,
// reverseDeletionRequests), since a client-side check can always be bypassed.
export async function isAdmin() {
    const { tokens } = await fetchAuthSession();
    const groups = tokens.idToken.payload['cognito:groups'] || [];
    return Array.isArray(groups) && groups.includes('admins');
}

// File a deletion request for any of the six entity types.
// Throws on failure — callers should catch and surface err.response?.data?.message
// (e.g. the 409 "blocked" or "already_pending" cases, or the 400 missing-reason case).
export async function requestDeletion(entityType, entityId, reason) {
    const token = await getIdToken();
    const res = await axios.post(`${config.API_URL}/deletion-requests`, {
        entity_type: entityType,
        entity_id: entityId,
        reason
    }, {
        headers: { Authorization: token }
    });
    return res.data;
}

// Admin: fetch the deletion queue with optional filters.
// filters: { entity_type, search, date_start, date_end, limit, offset }
export async function fetchDeletionQueue(filters = {}) {
    const token = await getIdToken();
    const res = await axios.get(`${config.API_URL}/admin/deletion-requests`, {
        headers: { Authorization: token },
        params: filters
    });
    return res.data;
}

// Admin: permanently delete one or more pending requests.
// Returns { results: [{ id, success, error? }] }
export async function permanentlyDeleteRequests(requestIds) {
    const token = await getIdToken();
    const res = await axios.post(`${config.API_URL}/admin/deletion-requests/permanently-delete`, {
        request_ids: requestIds
    }, {
        headers: { Authorization: token }
    });
    return res.data;
}

// Admin: reverse one or more pending requests, restoring visibility.
// Returns { results: [{ id, success, error? }] }
export async function reverseDeletionRequests(requestIds) {
    const token = await getIdToken();
    const res = await axios.post(`${config.API_URL}/admin/deletion-requests/reverse`, {
        request_ids: requestIds
    }, {
        headers: { Authorization: token }
    });
    return res.data;
}
